import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import type { TodoSwimLane } from '../../types';
import { TODO_LANE_LABELS } from '../../utils/todoSwimLanes';

interface Props {
  open: boolean;
  projectName: string;
  targetLane: TodoSwimLane;
  affectedActiveCount: number;
  skippedCompletedCount: number;
  busy: boolean;
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

/**
 * Flank-branded confirmation sheet for cascade actions.
 * No native dialogs (workspace `no-browser-dialogs` rule).
 */
export function CascadeConfirmSheet({
  open,
  projectName,
  targetLane,
  affectedActiveCount,
  skippedCompletedCount,
  busy,
  onConfirm,
  onCancel,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const laneLabel = TODO_LANE_LABELS[targetLane];
  const verbByLane: Record<TodoSwimLane, string> = {
    today: 'Move to Today',
    this_week: 'Plan for this week',
    later: 'Shelve to Later',
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Cascade ${projectName} to ${laneLabel}`}
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.45)' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        ref={ref}
        className="flank-card-elevated"
        style={{
          background: 'var(--color-surface-elevated)',
          borderRadius: 18,
          padding: 20,
          width: 'min(440px, calc(100vw - 32px))',
          boxShadow: '0 16px 40px rgba(0, 0, 0, 0.24)',
          border: '1px solid var(--color-border)',
        }}
      >
        <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
          Move <span className="font-bold">{projectName}</span> to {laneLabel}?
        </h2>
        <p className="text-sm text-[var(--color-text-secondary)] mt-2">
          {affectedActiveCount === 0 ? (
            'Nothing to move — every list in this project is already complete or unchanged.'
          ) : (
            <>
              This sets the {laneLabel.toLowerCase()} swim lane on{' '}
              <span className="font-medium text-[var(--color-text-primary)]">
                {affectedActiveCount} list{affectedActiveCount === 1 ? '' : 's'}
              </span>
              {skippedCompletedCount > 0 ? (
                <>
                  {' '}
                  ({skippedCompletedCount} completed list{skippedCompletedCount === 1 ? '' : 's'} will be skipped)
                </>
              ) : null}
              .
            </>
          )}
        </p>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flank-btn flank-btn-secondary text-sm px-4 py-2 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || affectedActiveCount === 0}
            onClick={onConfirm}
            className="flank-btn flank-btn-primary text-sm px-4 py-2 disabled:opacity-60"
          >
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" />
                Updating…
              </span>
            ) : (
              verbByLane[targetLane]
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
